import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  HeartPulse, Network, Waves, BrainCircuit, TreePine, Zap, Compass
} from 'lucide-react';

const LAYERS = [
  {
    index: '01',
    domain: 'biological_stability',
    title: 'Biological Stability',
    description: 'Sleep quality, metabolic resilience, cognitive energy foundation',
    icon: HeartPulse,
    scrollTarget: 'domain-score-breakdown',
    color: '#10B981',
    insights: {
      high: 'Recovery instability may be reducing cognitive stamina.',
      moderate: 'Biological rhythms show room for sleep and energy optimization.',
      balanced: 'Strong biological foundation supporting cognitive performance.'
    }
  },
  {
    index: '02',
    domain: 'brain_network_performance',
    title: 'Brain Network Performance',
    description: 'Attention control, working memory, neural efficiency',
    icon: Network,
    scrollTarget: 'mind-architecture-wheel',
    color: '#3B82F6',
    insights: {
      high: 'Attention fragmentation may be limiting working memory capacity.',
      moderate: 'Neural efficiency is functional but not yet optimized.',
      balanced: 'Attention networks are performing within healthy parameters.'
    }
  },
  {
    index: '03',
    domain: 'brainwave_regulation',
    title: 'Brainwave Regulation',
    description: 'Alpha, beta, theta balance and neural calm regulation',
    icon: Waves,
    scrollTarget: 'neural-friction-map',
    color: '#8B5CF6',
    insights: {
      high: 'High beta dominance may be interfering with calm focus.',
      moderate: 'Brainwave regulation shows intermittent stress-state patterns.',
      balanced: 'Neural calm regulation is stable and well-modulated.'
    }
  },
  {
    index: '04',
    domain: 'cognitive_patterns',
    title: 'Cognitive Patterns',
    description: 'Thought loops, interpretation habits and mental framing',
    icon: BrainCircuit,
    scrollTarget: 'cognitive-pattern-explanation',
    color: '#F59E0B',
    insights: {
      high: 'Repetitive thought loops may be distorting situational perception.',
      moderate: 'Some cognitive patterns could benefit from reframing exercises.',
      balanced: 'Thought processing is adaptive and contextually flexible.'
    }
  },
  {
    index: '05',
    domain: 'belief_architecture',
    title: 'Belief Architecture',
    description: 'Core belief structures influencing decisions and behavior',
    icon: TreePine,
    scrollTarget: 'belief-architecture-map',
    color: '#EC4899',
    insights: {
      high: 'Restrictive belief coding may be amplifying internal resistance.',
      moderate: 'Some belief structures are limiting decision flexibility.',
      balanced: 'Belief systems are supporting adaptive decision-making.'
    }
  },
  {
    index: '06',
    domain: 'behavioral_execution',
    title: 'Behavioral Execution',
    description: 'Consistency of action, discipline and follow-through',
    icon: Zap,
    scrollTarget: 'behavioral-insights',
    color: '#EF4444',
    insights: {
      high: 'Execution gaps suggest friction between intention and action.',
      moderate: 'Follow-through consistency has room for structured improvement.',
      balanced: 'Action-intention alignment is strong and reliable.'
    }
  },
  {
    index: '07',
    domain: 'identity_alignment',
    title: 'Identity & Purpose Alignment',
    description: 'Purpose clarity, self-concept coherence and long-term direction',
    icon: Compass,
    scrollTarget: 'upgrade-path-recommendation',
    color: '#14B8A6',
    insights: {
      high: 'Purpose fragmentation may be undermining motivational drive.',
      moderate: 'Self-concept clarity could be sharpened for stronger direction.',
      balanced: 'Identity coherence is intact with clear directional focus.'
    }
  }
];

function getStatus(healthScore) {
  if (healthScore >= 70) return { label: 'Balanced', dotColor: '#10B981', level: 'balanced' };
  if (healthScore >= 50) return { label: 'Moderate Friction', dotColor: '#F59E0B', level: 'moderate' };
  return { label: 'High Friction', dotColor: '#EF4444', level: 'high' };
}

export default function MindArchitectureLayers({ domainScores, navigable = false, compact = false }) {
  const navigate = useNavigate();
  const hasScores = domainScores && domainScores.length > 0;

  const scoreMap = hasScores
    ? Object.fromEntries(domainScores.map(ds => [ds.domain, ds]))
    : {};

  const handleClick = (layer) => {
    if (!navigable) return;
    const el = document.getElementById(layer.scrollTarget);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate('/report');
    }
  };

  return (
    <div className="w-full" data-testid="mind-architecture-layers">
      {/* Cards Grid */}
      <div className={`grid gap-4 ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-7'}`}>
        {LAYERS.map((layer, i) => {
          const Icon = layer.icon;
          const ds = scoreMap[layer.domain];
          const healthScore = ds ? Math.round(100 - ds.dysfunction_score) : null;
          const status = healthScore !== null ? getStatus(healthScore) : null;
          const insight = status ? layer.insights[status.level] : null;

          return (
            <motion.div
              key={layer.domain}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.4 }}
              className="relative group"
            >
              {/* Cascade connector — vertical on mobile */}
              {i > 0 && (
                <div className="md:hidden flex justify-center -mt-2 mb-2">
                  <div className="w-px h-4 bg-gradient-to-b from-border/60 to-border/20" />
                </div>
              )}
              {/* Cascade connector — horizontal on desktop */}
              {i > 0 && !compact && (
                <div className="hidden md:block absolute -left-4 top-1/2 w-4 h-px">
                  <div className="w-full h-full bg-gradient-to-r from-border/60 to-border/20" />
                </div>
              )}

              <div
                onClick={() => handleClick(layer)}
                data-testid={`layer-card-${layer.domain}`}
                className={`
                  relative overflow-hidden rounded-xl border border-border/50
                  bg-card p-5 h-full flex flex-col
                  transition-all duration-300
                  ${navigable ? 'cursor-pointer' : ''}
                  hover:border-border hover:shadow-lg hover:shadow-black/10
                  hover:-translate-y-0.5
                  group
                `}
                style={{ minHeight: hasScores ? 230 : 170 }}
              >
                {/* Glow border on hover */}
                <div
                  className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{ boxShadow: `inset 0 0 0 1px ${layer.color}40, 0 0 20px ${layer.color}10` }}
                />

                {/* Header: Index + Icon */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono tracking-wider text-muted-foreground">{layer.index}</span>
                  <Icon
                    className="transition-colors duration-300 text-muted-foreground/40 group-hover:text-muted-foreground/80"
                    style={{ width: 18, height: 18 }}
                    strokeWidth={1.5}
                  />
                </div>

                {/* Title */}
                <h4 className="text-sm font-semibold leading-snug tracking-tight mb-1.5">{layer.title}</h4>

                {/* Description — fixed 3-line height for uniform cards */}
                <p className="text-[11px] leading-relaxed text-muted-foreground" style={{ minHeight: 42 }}>
                  {layer.description}
                </p>

                {/* Score + Status (only with diagnostic data) */}
                {hasScores && healthScore !== null && (
                  <div className="mt-auto pt-3 space-y-2.5 border-t border-border/30">
                    {/* Explicit score label */}
                    <div className="flex items-baseline justify-between">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Score</span>
                      <span className="text-sm font-mono font-bold tabular-nums" style={{ color: status.dotColor }} data-testid={`layer-score-${layer.domain}`}>
                        {healthScore}<span className="text-muted-foreground font-normal text-[10px]"> / 100</span>
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${healthScore}%` }}
                        transition={{ delay: i * 0.08 + 0.3, duration: 0.6, ease: 'easeOut' }}
                        style={{ backgroundColor: status.dotColor }}
                      />
                    </div>

                    {/* Status badge */}
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: status.dotColor }} />
                      <span className="text-[10px] leading-none" style={{ color: status.dotColor }}>
                        {status.label}
                      </span>
                    </div>
                  </div>
                )}

                {/* Hover tooltip with diagnostic insight */}
                {insight && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-20 pointer-events-none">
                    <div className="bg-popover border border-border rounded-lg px-3 py-2.5 shadow-xl max-w-[240px]">
                      <p className="text-[11px] leading-snug text-foreground whitespace-normal">{insight}</p>
                    </div>
                    <div className="w-2 h-2 bg-popover border-b border-r border-border rotate-45 mx-auto -mt-1" />
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Cascade flow line (below cards on desktop) */}
      {!compact && (
        <div className="hidden md:flex items-center justify-center gap-0 mt-5 px-8">
          {LAYERS.map((layer, i) => {
            const ds = scoreMap[layer.domain];
            const hs = ds ? Math.round(100 - ds.dysfunction_score) : null;
            const st = hs !== null ? getStatus(hs) : null;
            return (
              <div key={layer.domain} className="flex items-center">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-mono font-bold border"
                  style={{
                    borderColor: st ? st.dotColor + '60' : 'hsl(var(--border))',
                    color: st ? st.dotColor : 'hsl(var(--muted-foreground))',
                    backgroundColor: st ? st.dotColor + '10' : 'transparent'
                  }}
                >
                  {layer.index}
                </div>
                {i < LAYERS.length - 1 && (
                  <div className="w-12 lg:w-16 xl:w-20 h-px bg-gradient-to-r from-border/60 via-border/30 to-border/60 mx-0.5" />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Cascade footnote */}
      <p className="text-center text-[11px] text-muted-foreground mt-4">
        Neural friction in one layer cascades to others. Foundation layers affect all higher systems.
      </p>
    </div>
  );
}
